import type { ReactNode } from 'react';

import { BoutiqueInfo } from '@/components/cms/BoutiqueInfo';
import { BoutiquesList } from '@/components/cms/BoutiquesList';
import { BoutiqueDetailAbout } from '@/components/boutiques/BoutiqueDetailAbout';
import { BoutiquesFindCta } from '@/components/boutiques/BoutiquesFindCta';
import { BoutiquesListingInfo } from '@/components/boutiques/BoutiquesListingInfo';
import type { CardType } from '@/components/cms/Card';
import type { StoreHit } from '@/lib/algolia';
import type { BoutiqueDetailContext } from '@/lib/boutique/placeholders';
import type { CMSContentItem, CMSFindAStore, CMSPlaceholder } from '@/lib/cms/types';
import { devWarn } from '@/lib/utils/devLog';

const MEDIA_SIDE_TEXT_CARD_TYPES = new Set([
  'MediaAndSideText',
  'ImageAndDidYouKnow',
  'MediaAndWatch',
]);

export function resolveBoutiqueCardContext(
  cardType: CardType | undefined,
  extraData?: Record<string, unknown>
): {
  boutiquesListing?: {
    countryCode: string;
    countryOptions: { code: string; label: string }[];
    storeName?: string;
  };
  boutiqueDetail?: BoutiqueDetailContext;
} {
  const boutiquesListing =
    cardType === 'BoutiqueHeroBanner'
      ? (extraData?.boutiquesList as
          | {
              countryCode: string;
              countryOptions: { code: string; label: string }[];
              storeName?: string;
            }
          | undefined)
      : undefined;

  const boutiqueDetail =
    cardType && MEDIA_SIDE_TEXT_CARD_TYPES.has(cardType)
      ? (extraData?.boutiqueDetail as BoutiqueDetailContext | undefined)
      : undefined;

  return { boutiquesListing, boutiqueDetail };
}

export function renderPlaceholderBoutiquesInfo(
  sysId: string,
  cmsIndex: number,
  extraData?: Record<string, unknown>
): ReactNode {
  const detail = extraData?.boutiqueDetail as BoutiqueDetailContext | undefined;

  if (!detail?.store) {
    devWarn('Missing boutiqueDetail extraData for PlaceholderBoutiquesInfo');
    return null;
  }

  return (
    <BoutiqueInfo
      key={sysId}
      store={detail.store}
      countryCode={detail.countryCode}
      cmsIndex={cmsIndex}
    />
  );
}

export function renderPlaceholderBoutiqueDetailAbout(
  sysId: string,
  extraData?: Record<string, unknown>
): ReactNode {
  const detail = extraData?.boutiqueDetail as BoutiqueDetailContext | undefined;

  if (!detail?.store) {
    devWarn('Missing boutiqueDetail extraData for BoutiqueDetailAbout placeholder');
    return null;
  }

  return <BoutiqueDetailAbout key={sysId} store={detail.store} />;
}

export function renderBoutiqueFindAStore(
  sysId: string,
  item: CMSFindAStore,
  extraData?: Record<string, unknown>
): ReactNode | undefined {
  const findAStoreLayout = extraData?.findAStoreLayout as string | undefined;
  const data = item.findAStoreData;
  const panelType = data?.type;

  if (findAStoreLayout !== 'boutique-panel' && panelType !== 'boutique-panel') {
    return undefined;
  }

  return (
    <BoutiquesFindCta
      key={sysId}
      href={data?.url ?? item.url ?? undefined}
      imageUrl={
        data?.longImage?.url ??
        data?.cardImage?.url ??
        item.longImage?.url ??
        item.cardImage?.url ??
        undefined
      }
    />
  );
}

export function renderPlaceholderBoutiquesList(
  sysId: string,
  extraData?: Record<string, unknown>
): ReactNode {
  const list = extraData?.boutiquesList as
    | {
        stores?: StoreHit[];
        countryCode?: string;
        countryOptions?: Array<{ code: string; label: string }>;
      }
    | undefined;

  if (!list?.countryCode || !list.stores) {
    devWarn('Missing boutiquesList extraData for boutiques list placeholder');
    return null;
  }

  return (
    <BoutiquesList
      key={sysId}
      stores={list.stores}
      countryCode={list.countryCode}
      countryOptions={list.countryOptions ?? []}
    />
  );
}

export function renderBoutiquePlaceholder(
  placeholderType: string | undefined,
  item: CMSContentItem,
  sysId: string,
  cmsIndex: number,
  extraData?: Record<string, unknown>
): ReactNode | undefined {
  switch (placeholderType) {
    case 'boutiques info': {
      const listing = extraData?.boutiquesListing as
        | {
            countryCode?: string;
            countryOptions?: Array<{ code: string; label: string }>;
          }
        | undefined;

      if (!listing?.countryCode || !listing.countryOptions?.length) {
        devWarn('Missing boutiquesListing extraData for boutiques info placeholder');
        return null;
      }

      return (
        <BoutiquesListingInfo
          key={sysId}
          placeholder={item as CMSPlaceholder}
          countryCode={listing.countryCode}
          countryOptions={listing.countryOptions}
        />
      );
    }

    case 'boutique info detail':
    case 'boutique detail info':
      return renderPlaceholderBoutiquesInfo(sysId, cmsIndex, extraData);

    case 'boutique detail about':
    case 'boutique story':
      return renderPlaceholderBoutiqueDetailAbout(sysId, extraData);

    default:
      return undefined;
  }
}
